<template>
  <fader>
    <item-container
      v-if="data"
      :loaded="meta.id"
      :id="`combatant-${id}`"
      :class="['c-combatant md:pb-4', { 'shadow is-active': data.active }]"
    >
      <template v-slot:before>
        <base-field
          :value="data.initiative || 0"
          @blur.native="updateInitiative"
          class="text-center md:leading-loose max-md:-mt-1 max-md:t-px max-md:pt-px relative md:-mt-px"
          :class="{ ' is-active--number ': data.active }"
          size="2"
          style="width: 1.2em;"
          title="Combatant's initiative roll"
          type="number"
        ></base-field>
      </template>

      <template v-slot:default>
        <display-field
          class="md:py-1"
          :primary="true"
          :focus="shouldFocusName"
          :value="meta.name || ``"
          @input="name => updateProps({ meta: { ...data.meta, name } })"
          placeholder="Enter combatant name"
        ></display-field>

        <div class="ml-2 inline-flex md:mr-2  md:mt-1 max-md:pr-2">
          <stats-link
            title="View combatant stats"
            collection="combatants"
            :id="id"
            :class="[$options.$styles.iconButtons]"
            >stats</stats-link
          >
        </div>
      </template>

      <template v-slot:secondary>
        <effects-list :effects="data.effects" :affected="id" />
      </template>

      <template v-slot:tertiary>
        <icon-button
          :value="meta.inspiration || 0"
          @slotUpdated="
            (e, inspiration) =>
              updateProps({ meta: { ...data.meta, inspiration } })
          "
          class="text-accent"
          title="Inspiration"
        >
          <icon data-id="inspiration" class="opacity-20" />
          <icon slot="on" data-id="inspiration" class="opacity-100" />
        </icon-button>

        <icon-button
          :value="data.advantage"
          @slotUpdated="(e, advantage) => updateProps({ advantage })"
          class="text-accent"
          title="Advantage / disadvantage"
        >
          <icon data-id="d20" class="opacity-20" />
          <icon class="opacity-100 fill-bg" slot="advantage" data-id="d20" />
          <icon class="opacity-100" slot="disdvantage" data-id="d20" />
        </icon-button>
      </template>

      <template v-slot:after>
        <button
          class="bg-transparent button-reset hover:bg-transparent hover:opacity-100 hover:text-accent o-button opacity-40 t text-accent py-4 xl:py-5 max-md:pt-3 max-md:mt-px"
          @click="() => destroy(id)"
          title="Remove combatant"
        >
          ×
        </button>
      </template>
    </item-container>
  </fader>
</template>

<script>
import BaseField from './BaseField'
import StatsLink from './StatsLink'
import DisplayField from './DisplayField'
import EffectsList from './EffectsList'
import Icon from './Icon'
import IconButton from './IconButton'
import ItemContainer from './ItemContainer'

import { mapActions, mapGetters, mapMutations } from 'vuex'
import { roll, updateProps } from '@/utilities/helpers'
import { db } from '@/utilities/db-helpers'
import { iconButtons } from '@/utilities/styles'

export default {
  name: `Combatant`,

  components: {
    BaseField,
    StatsLink,
    DisplayField,
    EffectsList,
    Icon,
    IconButton,
    ItemContainer,
  },

  filters: {
    number(val) {
      return val * 1
    },
  },

  data: () => ({
    meta: {},
  }),

  props: {
    data: {
      type: Object,
      required: true,
      validator: function({ meta, initiative, advantage, inspiration }) {
        return (
          typeof meta.id === `string` &&
          (typeof initiative === `undefined` || Number.isInteger(initiative)) &&
          (typeof advantage === `undefined` || Number.isInteger(advantage)) &&
          (typeof inspiration === `undefined` || Number.isInteger(inspiration))
        )
      },
    },
  },

  computed: {
    ...mapGetters([`battle`]),

    id() {
      return this.data.meta.id
    },

    shouldFocusName() {
      return this.meta.name === ``
    },
  },

  beforeDestroy() {
    this.unsubInitiativeListener()
  },

  created() {
    this.bindData(this.data.meta.id)
  },

  mounted() {
    this.unsubInitiativeListener = this.$store.subscribe(
      this.initiativeListenerWithId
    )
  },

  watch: {
    meta(newMeta) {
      if (this.data.active) this.setTurnName(newMeta.name)
    },
    data(newData) {
      if (newData.active) this.setTurnName(this.meta.name)
    },
  },

  methods: {
    ...mapActions(`combatant`, [`update`, `destroy`]),
    ...mapMutations(`battle/turns`, {
      setTurnName: `setName`,
    }),

    initiativeListenerWithId({ type, payload }) {
      if (type === `battle/setInitiativeStatus` && payload === true) {
        this.update({
          id: this.id,
          initiative: roll(1, 20) + (this.meta.initiative || 0),
        })
      }
    },

    async bindData(id) {
      return await db
        .ref(`/combatants/${id}`)
        .on(`value`, snapshot => (this.meta = snapshot.val()))
    },

    updateProps,

    updateInitiative(event) {
      this.updateProps({ initiative: event.target.value * 1 })
    },
  },

  $styles: {
    iconButtons,
  },
}
</script>

<style scoped>
.c-combatant::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
      100deg,
      var(--bg-fade-full-trans-color),
      var(--bg-fade-trans-color),
      var(--bg-fade-full-trans-color)
    )
    no-repeat;
  transform: scaleX(0.5) skewX(-85deg);
  transform-origin: 50%;
  opacity: 0;
  transition: all 0.3s ease-in-out;
}
.is-active::before {
  opacity: 0.8;
  transform: scaleX(1);
}
</style>

<style lang="postcss">
@keyframes pulse {
  0% {
    color: inherit;
    opacity: 0;
  }
  25% {
    opacity: 1;
    color: white;
    filter: var(--glow-soft);
    transform: translateY(-50%) translateX(20%);
  }
  50% {
    opacity: 0.25;
    color: inherit;
    filter: none;
    transform: translateY(-50%);
  }

  75% {
    opacity: 1;
    color: white;
    filter: var(--glow-soft);
    transform: translateY(-50%) translateX(20%);
  }

  100% {
    color: inherit;
    opacity: 0.4;
  }
}

@screen lg {
  .c-item__before::after {
    content: '➜';
    position: absolute;
    display: none;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    margin-left: -2rem;
    z-index: 0;
    animation: pulse 1.75s;
    animation-fill-mode: forwards;
  }
  .is-active .c-item__before::after {
    display: block;
  }
}
</style>
